// Vendor
import { FC } from 'react';
import { RentVerificationStatus } from 'src/api/rentVerificationApi';
import Button, { ButtonSize, ButtonVariant } from 'src/components/atoms/Button';
import { Icon } from 'src/components/atoms/Icon';
import Table from 'src/components/atoms/Table';
import TableBody from 'src/components/atoms/TableBody';
import TableCell from 'src/components/atoms/TableCell';
import TableContainer from 'src/components/atoms/TableContainer';
import TableHead from 'src/components/atoms/TableHead';
import TableRow from 'src/components/atoms/TableRow';
import Text, { TextColor, TextVariant } from 'src/components/atoms/Text';
import { ToolTip } from 'src/components/atoms/ToolTip';
import { ToolTipDirection } from 'src/components/atoms/ToolTip/ToolTip';
import { configVars } from 'src/helpers';
import { RentVerificationTableItem } from 'src/pages/FDEInboxPage/components/RentVerificationList';
import { RoleType } from 'src/ts/interfaces';
import { rentVerificationColumns } from './constants';
import {
  ActionContainer,
  ButtonContent,
  Content,
  Header,
  HeaderInfo,
  StyledIcon,
  StyledTag,
  StyledText
} from './styles';
import { getReportCellText } from './utils';

export interface RentVerificationRowProps {
  verification: RentVerificationTableItem;
  role: RoleType;
}

const RentVerificationRow: FC<RentVerificationRowProps> = (props) => {
  const { verification, role } = props;
  if (!role || !verification) {
    return null;
  }

  const handleDownloadReport = () => {
    window.location.href = `${configVars.epa_url}/internal/rent-verifications/${verification.applicantId}/report`;
  };

  const handleDownloadDraftReport = () => {
    window.location.href = `${configVars.epa_url}/internal/rent-verifications/${verification.applicantId}/draft-report`;
  };

  const hasReportUrl = verification.reportUrl && verification.reportUrl.trim() !== '';
  const isReportReady = verification.status === RentVerificationStatus.COMPLETED && hasReportUrl;

  return (
    <Content>
      <Header>
        <HeaderInfo>
          <Icon icon="person-icon" color="gray900" />
          <StyledText variant={TextVariant.h4} color={TextColor.gray700}>
            {verification.applicantName}
          </StyledText>
        </HeaderInfo>
        <HeaderInfo>
          <Icon icon="company-model-icon" color="gray700" />
          <StyledText variant={TextVariant.h4} color={TextColor.gray700}>
            {verification.propertyName}
          </StyledText>
        </HeaderInfo>
      </Header>

      <TableContainer className="rent-verification-table">
        <Table>
          <TableHead>
            <TableRow>
              {rentVerificationColumns.map((column) => (
                <TableCell key={column.name} width={column.width}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow key={verification.id}>
              <TableCell width={rentVerificationColumns[0].width}>
                <Text variant={TextVariant.small} color={TextColor.gray700}>
                  {verification.insertedAt}
                </Text>
              </TableCell>
              <TableCell width={rentVerificationColumns[1].width}>
                <Text variant={TextVariant.small} color={TextColor.gray700}>
                  {verification.processingTime}
                </Text>
              </TableCell>
              <TableCell width={rentVerificationColumns[2].width}>
                <StyledTag status={verification.status as RentVerificationStatus} />
              </TableCell>
              <TableCell width={rentVerificationColumns[3].width}>
                <ActionContainer>
                  {isReportReady ? (
                    <ToolTip
                      content="The report is ready for review."
                      direction={ToolTipDirection.top}
                    >
                      <Button
                        name="download-report"
                        variant={ButtonVariant.ghost}
                        size={ButtonSize.small}
                        onClick={handleDownloadReport}
                      >
                        <ButtonContent>
                          <StyledIcon icon="download-icon" />
                          <Text variant={TextVariant.small}>Report Ready</Text>
                        </ButtonContent>
                      </Button>
                    </ToolTip>
                  ) : verification.status === RentVerificationStatus.PENDING ? (
                    <ToolTip
                      content="We're verifying the applicant's rental history now."
                      direction={ToolTipDirection.top}
                    >
                      <Button
                        name="download-draft-report"
                        variant={ButtonVariant.ghost}
                        size={ButtonSize.small}
                        onClick={handleDownloadDraftReport}
                      >
                        <ButtonContent>
                          <StyledIcon icon="download-icon" />
                          <Text variant={TextVariant.small}>Draft Ready</Text>
                        </ButtonContent>
                      </Button>
                    </ToolTip>
                  ) : (
                    <Text variant={TextVariant.small} color={TextColor.gray500}>
                      {getReportCellText(verification.status as RentVerificationStatus)}
                    </Text>
                  )}
                </ActionContainer>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Content>
  );
};

export default RentVerificationRow;
